import React from "react";
import { Breadcrumb, Button, Space } from "antd";
import { HomeOutlined, SaveOutlined, ArrowLeftOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ── design tokens (follow html.pos-dark / light CSS vars) ─────────── */
export const T = {
  bg:      "var(--velan-bg, #f1f5f9)",
  card:    "var(--velan-card, #ffffff)",
  border:  "var(--velan-border, #e2e8f0)",
  borderL: "var(--velan-border-light, #f1f5f9)",
  primary: "var(--velan-primary, #2563eb)",
  text:    "var(--velan-text, #0f172a)",
  sub:     "var(--velan-sub, #64748b)",
  muted:   "var(--velan-muted, #94a3b8)",
  red:     "var(--velan-red, #e11d48)",
  green:   "var(--velan-green, #16a34a)",
};

export const cardStyle = {
  background: T.card,
  borderRadius: 14,
  border: `1px solid ${T.border}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  overflow: "hidden",
};

/** Readable money / qty text for tables and tags (keeps thousand separators visible). */
export function Money({ children, color, strong = false, style }) {
  return (
    <span
      className="velan-money"
      style={{
        color,
        fontWeight: strong ? 700 : undefined,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/* ── StatCard ──────────────────────────────────────────────────────── */
/** Stacked metric card: full amount always visible (no inner horizontal scroll). */
export function StatCard({ icon, label, value, iconBg, valueColor = T.text, onClick, className = "", locked = false, lockedHint }) {
  const clickable = typeof onClick === "function" && !locked;
  const valueText = value == null ? "" : String(value);
  const valueLen = valueText.replace(/\s/g, "").length;
  // Shrink long IQD amounts so they stay fully readable inside the card.
  const valueFontSize = valueLen > 18
    ? "clamp(0.92rem, 1.8vw + 0.4rem, 1.2rem)"
    : valueLen > 12
      ? "clamp(1.02rem, 2vw + 0.35rem, 1.4rem)"
      : "clamp(1.15rem, 2.4vw, 1.65rem)";

  return (
    <div
      className={`velan-stat-card${clickable ? " velan-stat-card--clickable" : ""}${locked ? " velan-stat-card--locked" : ""}${className ? ` ${className}` : ""}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={locked ? undefined : onClick}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
      title={locked ? lockedHint : undefined}
      style={{
        ...cardStyle,
        overflow: "visible",
        maxWidth: "100%",
        minWidth: 0,
        width: "100%",
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 10,
        minHeight: 108,
        cursor: clickable ? "pointer" : (locked ? "not-allowed" : undefined),
        position: "relative",
      }}
    >
      {locked && (
        <div
          className="velan-stat-card__lock"
          style={{
            position: "absolute",
            top: 10,
            insetInlineEnd: 10,
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "rgba(15, 23, 42, 0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: T.muted,
            fontSize: 12,
            pointerEvents: "none",
          }}
        >
          <LockOutlined />
        </div>
      )}
      <div className="velan-stat-card__head" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {icon != null && (
          <div
            className="velan-stat-card__icon"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: iconBg || "var(--velan-border-light, #f1f5f9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
        )}
        <div
          className="velan-stat-card__label"
          style={{
            fontSize: 13,
            color: T.muted,
            fontWeight: 700,
            lineHeight: 1.35,
            minWidth: 0,
            flex: 1,
          }}
        >
          {label}
        </div>
      </div>
      {locked ? (
        <div
          className="velan-stat-card__value-skeleton"
          style={{ height: 26, width: "60%", borderRadius: 6 }}
        />
      ) : (
        <div
          className={`velan-stat-card__value${valueLen > 12 ? " velan-stat-card__value--long" : ""}${valueLen > 18 ? " velan-stat-card__value--xl" : ""}`}
          style={{
            fontSize: valueFontSize,
            fontWeight: 800,
            color: valueColor,
            lineHeight: 1.3,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "0.01em",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            whiteSpace: "normal",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          {value}
        </div>
      )}
      {locked && lockedHint && (
        <div
          className="velan-stat-card__locked-hint"
          style={{
            fontSize: 11,
            color: T.muted,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <LockOutlined style={{ fontSize: 10 }} />
          {lockedHint}
        </div>
      )}
    </div>
  );
}

/* ── PageHeader ────────────────────────────────────────────────────── */
export function PageHeader({ breadcrumb, title, subtitle, action }) {
  return (
    <div className="velan-page-header" style={{ marginBottom: 20 }}>
      {breadcrumb && (
        <Breadcrumb
          style={{ marginBottom: 8, fontSize: 13 }}
          items={[
            { title: <HomeOutlined /> },
            ...breadcrumb.map((b) =>
              typeof b === "string" ? { title: b } : b
            ),
          ]}
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0, flex: "1 1 220px" }}>
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(1.2rem, 3vw, 1.4rem)",
              fontWeight: 800,
              color: T.text,
              letterSpacing: "-0.02em",
              lineHeight: 1.25,
            }}
          >
            {title}
          </h1>
          {subtitle && (
            <p style={{ margin: "4px 0 0", fontSize: 13, color: T.sub, lineHeight: 1.45, maxWidth: 640 }}>
              {subtitle}
            </p>
          )}
        </div>
        {action && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" }}>{action}</div>
        )}
      </div>
    </div>
  );
}

/* ── ListShell ─────────────────────────────────────────────────────── */
export function ListShell({ toolbar, children, footer }) {
  return (
    <div style={cardStyle} className="velan-list-shell">
      {toolbar && (
        <div className="velan-list-toolbar" style={{ padding: "12px 16px", borderBottom: `1px solid ${T.borderL}`, background: "var(--velan-border-light, #fafafa)" }}>
          {toolbar}
        </div>
      )}
      <div className="velan-list-body">{children}</div>
      {footer && (
        <div className="velan-list-footer" style={{ padding: "10px 16px", borderTop: `1px solid ${T.borderL}`, background: "var(--velan-border-light, #fafafa)", display: "flex", gap: 16, flexWrap: "wrap" }}>
          {footer}
        </div>
      )}
    </div>
  );
}

/* ── FormCard ──────────────────────────────────────────────────────── */
export function FormCard({ title, subtitle, children }) {
  return (
    <div style={cardStyle} className="velan-form-card">
      <div style={{ padding: "16px 22px" }}>
        <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${T.borderL}` }}>
          <div className="velan-form-card__title" style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{title}</div>
          {subtitle && <div className="velan-form-card__subtitle" style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── FormPage wrapper ──────────────────────────────────────────────── */
export function FormPage({ breadcrumb, title, subtitle, onBack, onSave, saving, children, sidePanel }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleBack = onBack || (() => navigate(-1));

  return (
    <div className="velan-page" style={{ padding: "clamp(12px, 2.5vw, 24px)", background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={breadcrumb}
        title={title}
        subtitle={subtitle}
        action={
          <Space wrap>
            <Button onClick={handleBack} style={{ borderRadius: 9, height: 38 }} icon={<ArrowLeftOutlined />}>
              {t("common.back", { defaultValue: "Back" })}
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave} style={{ borderRadius: 9, fontWeight: 700, height: 38, paddingInline: 20 }}>
              {t("common.save", { defaultValue: "Save" })}
            </Button>
          </Space>
        }
      />
      <div className={`velan-form-layout${sidePanel ? " velan-form-layout--with-side" : ""}`}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          {children}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Button type="primary" icon={<SaveOutlined />} size="large" loading={saving} onClick={onSave} style={{ borderRadius: 10, fontWeight: 700, flex: 1, minWidth: 140, height: 46 }}>
              {t("common.save", { defaultValue: "Save" })}
            </Button>
            <Button size="large" onClick={handleBack} style={{ borderRadius: 10, height: 46, minWidth: 110 }}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
          </div>
        </div>
        {sidePanel && <div className="velan-form-side" style={{ display: "flex", flexDirection: "column", gap: 16 }}>{sidePanel}</div>}
      </div>
    </div>
  );
}

/* ── ListPage wrapper ──────────────────────────────────────────────── */
export function ListPage({ breadcrumb, title, subtitle, action, stats, toolbar, children, footer, compact = false }) {
  return (
    <div
      className="velan-page"
      style={{
        padding: compact ? 0 : "clamp(12px, 2.5vw, 24px)",
        background: compact ? "transparent" : T.bg,
        minHeight: compact ? "auto" : "100%",
      }}
    >
      <PageHeader breadcrumb={breadcrumb} title={title} subtitle={subtitle} action={action} />
      {stats && <div className="velan-stats-row">{stats}</div>}
      <ListShell toolbar={toolbar} footer={footer}>
        {children}
      </ListShell>
    </div>
  );
}

/* ── input helpers ─────────────────────────────────────────────────── */
export const inputStyle  = { borderRadius: 9, height: 42 };
export const labelStyle  = { fontWeight: 600, fontSize: 13, color: T.text };
export const grid2       = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: "0 16px" };
export const grid3       = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: "0 16px" };
